import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TodayIcon from '@mui/icons-material/Today';
import Badge from '@mui/material/Badge';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { useQueryClient } from '@tanstack/react-query';
import addYears from 'date-fns/addYears';
import isBefore from 'date-fns/isBefore';
import lastDayOfMonth from 'date-fns/lastDayOfMonth';
import startOfToday from 'date-fns/startOfToday';
import { useMemo, useState } from 'react';
import { useController, useWatch } from 'react-hook-form';

import {
  useAccessToken,
  useSlug
} from 'reservations/account/providers/ConfigProvider';
import { AVAILABILITY_STATUS } from 'reservations/availabilities/constants';
import { AVAILABILITIES_INPUT_SX } from 'reservations/availabilities/constants';
import { getDayStyles } from 'reservations/availabilities/helpers/getDayStyles';
import { getCalendarAvailabilities } from 'reservations/availabilities/queries/useCalendarAvailabilitiesQuery';
import type { GetAvailabilities } from 'reservations/availabilities/types';
import LoadingSpinner from 'reservations/base/components/LoadingSpinner';
import { REDIRECT_WITH_ACCOUNT_DETAILS } from 'reservations/base/constants/redirects';
import { COMMON_DATETIME_FORMATS } from 'reservations/base/helpers/datetime';
import is5xxError from 'reservations/base/helpers/is5xxError';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';

type FormattedAvailabilities = {
  [key: string]: number;
};

// istanbul ignore next
const formatAvailabilities = (data: GetAvailabilities) => {
  const availabilities = Object.values(data.availability);
  const formattedAvailabilities: FormattedAvailabilities = {};
  availabilities.forEach(day => {
    const [dayString, _time] = day.date.split('T');
    formattedAvailabilities[dayString] = day.status;
  });
  return formattedAvailabilities;
};

const DatePicker = () => {
  const { handleNavigation } = useCustomRouter();
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();
  const slug = useSlug();
  const theme = useTheme();
  const primaryColor = theme.typography.body1.color;
  const backgroundColor = theme.palette.background.default;
  const {
    field: { onChange, value, name, ref }
  } = useController({
    name: 'date'
  });
  const [partySize] = useWatch({
    name: ['partySize']
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isMonthLoading, setIsMonthLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(value);
  const [currentMonthAvailabilities, setCurrentMonthAvailabilities] =
    useState<FormattedAvailabilities>({});

  const firstDateOfMonth = useMemo(
    () => COMMON_DATETIME_FORMATS.YMM01(currentMonth),
    [currentMonth]
  );
  const lastDateOfMonth = useMemo(
    () => COMMON_DATETIME_FORMATS.YMMdd(lastDayOfMonth(currentMonth)),
    [currentMonth]
  );
  const currentlySelectedDate = useMemo(
    () => COMMON_DATETIME_FORMATS.dayTruncated(value),
    [value]
  );

  // istanbul ignore next
  const getCalendarAvailabilitiesQuery = async () => {
    if (
      !!partySize &&
      !!firstDateOfMonth &&
      !!lastDateOfMonth &&
      !!accessToken &&
      !!slug
    ) {
      try {
        const data = await queryClient.fetchQuery(
          getCalendarAvailabilities({
            partySize,
            beginDateTime: firstDateOfMonth,
            endDateTime: lastDateOfMonth,
            apiKey: accessToken,
            slug,
            excludeTimeSlots: true
          })
        );
        const formattedAvailabilities = formatAvailabilities(data);
        setCurrentMonthAvailabilities(formattedAvailabilities);
      } catch (error: any) {
        console.error(error);
        if (error?.code) {
          if (is5xxError(error?.code)) {
            handleNavigation(REDIRECT_WITH_ACCOUNT_DETAILS);
          }
        }
      }
      setIsMonthLoading(false);
    }
  };

  // istanbul ignore next
  const handleMonthChange = async (date: Date) => {
    setIsMonthLoading(true);
    const firstDateOfMonth = COMMON_DATETIME_FORMATS.YMM01(date);
    const lastDateOfMonth = COMMON_DATETIME_FORMATS.YMMdd(lastDayOfMonth(date));
    try {
      const data = await queryClient.fetchQuery(
        getCalendarAvailabilities({
          partySize,
          beginDateTime: firstDateOfMonth,
          endDateTime: lastDateOfMonth,
          apiKey: accessToken,
          slug,
          excludeTimeSlots: true
        })
      );
      const formattedAvailabilities = formatAvailabilities(data);
      setCurrentMonthAvailabilities(formattedAvailabilities);
      setCurrentMonth(date);
      setIsMonthLoading(false);
    } catch (error: any) {
      console.error(error);
      if (error?.code) {
        if (is5xxError(error?.code)) {
          handleNavigation(REDIRECT_WITH_ACCOUNT_DETAILS);
        }
      }
    }
  };

  const onModalOpen = () => {
    setIsDatePickerOpen(true);
    setIsMonthLoading(true);
    getCalendarAvailabilitiesQuery();
  };

  const isStatusClosed = (day: string) =>
    currentMonthAvailabilities[day] === AVAILABILITY_STATUS.CLOSED;

  const isStatusAvailable = (day: string) =>
    currentMonthAvailabilities[day] === AVAILABILITY_STATUS.AVAILABLE;

  const isStatusUnavailable = (day: string) =>
    currentMonthAvailabilities[day] === AVAILABILITY_STATUS.UNAVAILABLE;

  const handleChange = (date: Date | null) => {
    onChange(date);
  };

  const yearInAdvance = addYears(new Date(), 1);

  return (
    <MuiDatePicker
      data-testid="date-picker"
      closeOnSelect
      componentsProps={{
        actionBar: {
          actions: []
        },
        leftArrowButton: {
          sx: {
            color: primaryColor
          }
        },
        rightArrowButton: {
          sx: {
            color: primaryColor
          }
        }
      }}
      PopperProps={{
        title: 'Select Your Reservation Date',
        sx: {
          '& .MuiDayPicker-weekDayLabel': {
            color: theme => theme.typography.body1.color,
            fontSize: '16px'
          }
        }
      }}
      views={['day']}
      disablePast
      maxDate={yearInAdvance}
      label="Date"
      loading={isMonthLoading}
      renderLoading={() => <LoadingSpinner message="Loading Dates..." />}
      components={{
        OpenPickerIcon: isDatePickerOpen
          ? KeyboardArrowUpIcon
          : KeyboardArrowDownIcon
      }}
      onChange={handleChange}
      value={value}
      ref={ref}
      InputProps={{
        name,
        startAdornment: (
          <InputAdornment position="start">
            <TodayIcon />
          </InputAdornment>
        )
      }}
      renderInput={({ inputProps, ...props }) => (
        <TextField
          {...props}
          inputProps={{
            ...inputProps,
            readOnly: true,
            value: currentlySelectedDate
          }}
          sx={AVAILABILITIES_INPUT_SX}
          fullWidth
          data-testid="date-picker"
          onClick={onModalOpen}
        />
      )}
      open={isDatePickerOpen}
      onMonthChange={date => handleMonthChange(date)}
      onOpen={onModalOpen}
      onClose={() => setIsDatePickerOpen(false)}
      renderDay={(day, _value, DayComponentProps) => {
        const isPastDate = isBefore(day, startOfToday());
        const formattedDay = COMMON_DATETIME_FORMATS.YMMdd(day);
        const isClosed = isStatusClosed(formattedDay);
        const isAvailable = isStatusAvailable(formattedDay);
        const isUnavailable = isStatusUnavailable(formattedDay);
        const { badgeStyles, dayStyles } = getDayStyles({
          ...DayComponentProps,
          isPastDate,
          isClosed,
          isAvailable,
          isUnavailable,
          //@ts-ignore
          primaryColor,
          backgroundColor
        });

        return (
          <Badge
            data-testid="day-wrapper"
            key={day.toString()}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                left: '50%',
                pointerEvents: 'none'
              },
              '& .MuiPickersDay-dayWithMargin': {
                fontSize: '16px'
              },
              '& .MuiPickersDay-today': {
                height: '32px',
                width: '32px',
                border: `1px solid ${primaryColor} !important`
              },
              '& .MuiPickersDay-root': {
                background: backgroundColor,
                color: primaryColor
              }
            }}
            badgeContent={
              badgeStyles ? (
                /* istanbul ignore next */ <div style={badgeStyles} />
              ) : null
            }
          >
            <PickersDay
              {...DayComponentProps}
              disabled={DayComponentProps.disabled || isClosed}
              sx={dayStyles}
            />
          </Badge>
        );
      }}
    />
  );
};

export default DatePicker;
